import { Outlet, createFileRoute, useLocation, useNavigate } from '@tanstack/react-router';
import { type JSX, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  OpsShell,
  ProfileMenu,
  SENTINEL_DARK_LOGO_URL,
  useBranding,
  type OpsShellNavItem,
  type ProfileMenuLanguage,
} from '@sentinel/ui';
import { useSession } from '@sentinel/auth-core';
import {
  AuditLogIcon,
  ConnectedAppsIcon,
  CredentialsIcon,
  DataIcon,
  McpIcon,
  OverviewIcon,
  ProfileIcon,
  SecurityIcon,
  SessionsIcon,
} from '../features/account/components/nav-icons';
import { useLogout } from '../features/logout/use-logout';
import { loginRedirectSearch } from '../features/mcp/login-return';
import { useMcpServerInfo } from '../features/mcp/use-mcp-server-info';

const LANGUAGE_CODES = ['en', 'zh-TW', 'zh-CN', 'ja'] as const;

interface NavDef {
  to:
    | '/account'
    | '/account/profile'
    | '/account/security'
    | '/account/credentials'
    | '/account/sessions'
    | '/account/connected-apps'
    | '/account/mcp'
    | '/account/data'
    | '/account/audit-log';
  labelKey: string;
  icon: () => JSX.Element;
  /** Hidden unless the server serves MCP. */
  requiresMcp?: boolean;
}

const NAV_ITEMS: NavDef[] = [
  { to: '/account', labelKey: 'nav.overview', icon: OverviewIcon },
  { to: '/account/profile', labelKey: 'nav.profile', icon: ProfileIcon },
  { to: '/account/security', labelKey: 'nav.security', icon: SecurityIcon },
  { to: '/account/credentials', labelKey: 'nav.credentials', icon: CredentialsIcon },
  { to: '/account/sessions', labelKey: 'nav.sessions', icon: SessionsIcon },
  { to: '/account/connected-apps', labelKey: 'nav.connectedApps', icon: ConnectedAppsIcon },
  { to: '/account/mcp', labelKey: 'nav.mcp', icon: McpIcon, requiresMcp: true },
  { to: '/account/audit-log', labelKey: 'nav.auditLog', icon: AuditLogIcon },
  { to: '/account/data', labelKey: 'nav.data', icon: DataIcon },
];

function AccountLayout() {
  const { user, loading } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation('account');
  const branding = useBranding();
  const brandLogoSrc = branding.logoUrl ?? SENTINEL_DARK_LOGO_URL;
  const logout = useLogout();
  const mcpInfo = useMcpServerInfo();
  const mcpEnabled = mcpInfo.data?.enabled === true;

  useEffect(() => {
    if (loading || user) return;
    void navigate({
      to: '/login',
      replace: true,
      search: loginRedirectSearch(location.pathname, location.searchStr),
    });
  }, [user, loading, navigate, location.pathname, location.searchStr]);

  const pathname = location.pathname.replace(/\/$/, '') || '/account';

  const navItems: OpsShellNavItem[] = useMemo(
    () =>
      NAV_ITEMS.filter((item) => !item.requiresMcp || mcpEnabled).map((item) => {
        // Prefix match is intended for every item, including /account/mcp: the
        // MCP step-up and reveal screens (/account/mcp/step-up, /account/mcp/reveal/$id)
        // belong to the same feature, so the entry stays highlighted there.
        const isActive =
          item.to === '/account'
            ? pathname === '/account'
            : pathname === item.to || pathname.startsWith(`${item.to}/`);
        const Icon = item.icon;
        return {
          key: item.to,
          label: t(item.labelKey),
          icon: <Icon />,
          active: isActive,
          onClick: () => {
            void navigate({ to: item.to });
          },
        };
      }),
    [navigate, pathname, t, mcpEnabled],
  );

  const languages: ProfileMenuLanguage[] = useMemo(
    () =>
      LANGUAGE_CODES.map((code) => ({
        code,
        label: t(`languages.${code}`),
      })),
    [t],
  );

  async function handleLogout() {
    try {
      await logout.mutateAsync();
    } finally {
      // Always navigate even if the mutation errored — the cookies are gone
      // either way and SessionProvider will redirect on the next mount.
      window.location.assign('/login');
    }
  }

  function handleLanguageChange(code: string) {
    void i18n.changeLanguage(code);
  }

  // SessionProvider.refresh() sets loading: true while keeping the existing
  // user, so this guard must not unmount the /account subtree during a
  // re-validation (e.g. after a name save) — that would wipe the success
  // banner and any pending-email or password-terminal panel before they can
  // paint. A loading pass with a user already present is a re-validation,
  // not a first load; the redirect effect above is unaffected, since it
  // already only fires on `!loading && !user`.
  if (loading && !user) return null;

  // user can be null past this point too: the guard above only blocks a
  // loading-with-no-user pass, not a settled unauthenticated one, which
  // renders for one tick before the redirect effect above navigates away.
  return (
    <OpsShell
      brandLogo={<img src={brandLogoSrc} alt="" height={32} aria-hidden />}
      productName={t('product_name')}
      nav={navItems}
      navItemTestId={(key) => `account-nav-${key.replace(/^\//, '').replace(/\//g, '-') || 'overview'}`}
      collapsedStorageKey="sentinel.portal.sidenav.collapsed"
      collapseLabel={t('sidenav.collapse')}
      expandLabel={t('sidenav.expand')}
      userMenu={
        <ProfileMenu
          user={{ name: user?.name ?? null, email: user?.email ?? null }}
          languages={languages}
          currentLanguage={i18n.resolvedLanguage ?? i18n.language}
          onLanguageChange={handleLanguageChange}
          onLogout={handleLogout}
          labels={{
            openMenu: t('profile_menu.open'),
            language: t('profile_menu.language'),
            logout: t('profile_menu.logout'),
          }}
          testIdPrefix="portal-profile"
        />
      }
    >
      <Outlet />
    </OpsShell>
  );
}

export const Route = createFileRoute('/account')({
  component: AccountLayout,
});
